import { type FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import Dota2GoldIcon from 'app/icons/dota2_gold.svg';
import Dota2KillIcon from 'app/icons/dota2_kill.svg';
import LolGoldIcon from 'app/icons/lol_gold.svg';
import LolKillIcon from 'app/icons/lol_kill.svg';
import CheckIcon from 'app/icons/check.svg';
import TrophyIcon from 'app/icons/trophy.svg';
import { isNumber } from 'app/utils/types';
import { round } from 'app/utils/number';
import HeroesIcon from 'app/components/HeroesIcon';
import { formatTimer } from 'app/utils/entities/match_data';
import { sort, sortAsModel } from 'app/utils/array';
import { LOL_PLAYERS_ROLE_ORDER } from 'app/config/videogameSpecific.config';

import style from './style.module.scss';

interface Props {
  game: BracketGameDota | BracketGameLol;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
  videogameSlug: Subset<VideogameSlug, 'dota-2' | 'league-of-legends'>;
  oddStyle: boolean;
}

const getOrderedHeroes = (
  opponent: BracketGameOpponentDota | BracketGameOpponentLol | undefined
): Hero[] => {
  if (opponent && opponent.players) {
    if (Array.isArray(opponent.players)) {
      return sort(opponent.players, player => player.position).map(player => player.hero);
    }
    return sortAsModel(
      Object.entries(opponent.players),
      LOL_PLAYERS_ROLE_ORDER,
      playerWithRole => playerWithRole[0]
    )
      .map(playerWithRole => playerWithRole[1])
      .filter(player => player !== null)
      .map((player: BracketGamePlayerLol) => player.champion);
  }
  return [];
};

const getForfeitedOpponent = (game: BracketGameDota | BracketGameLol): number | null => {
  if (game.forfeit && game.winner_id) {
    return (
      game.opponents
        .map(({ id }) => id)
        .filter(id => id !== game.winner_id)
        .at(0) ?? null
    );
  }
  return null;
};

const BracketGameRecapMoba: FC<Props> = ({
  game,
  opponent1,
  opponent2,
  videogameSlug,
  oddStyle
}) => {
  const { t } = useTranslation();

  const winnerId = game.winner_id;

  const gameOpponent1 = game.opponents.find(opponent => opponent.id === opponent1?.id);
  const gameOpponent2 = game.opponents.find(opponent => opponent.id === opponent2?.id);

  const KillIcon = videogameSlug === 'dota-2' ? Dota2KillIcon : LolKillIcon;
  const GoldIcon = videogameSlug === 'dota-2' ? Dota2GoldIcon : LolGoldIcon;

  const opponent1Heroes = getOrderedHeroes(gameOpponent1);
  const opponent2Heroes = getOrderedHeroes(gameOpponent2);

  const forfeitedOpponent = getForfeitedOpponent(game);

  if (game.status === 'finished' && !game.complete) {
    return null;
  }

  if (game.status === 'not_started') {
    return null;
  }

  if (game.status === 'running') {
    return (
      <div
        className={classNames(style.gameRecap, style.runningGame, { [style.oddStyle]: oddStyle })}
      >
        <div className={style.timer}>{game.timer ? formatTimer(game.timer) : '--:--'}</div>
        <div>{t('component_bracket-match-recap_map-in-progress')}</div>
      </div>
    );
  }

  return (
    <div className={classNames(style.gameRecap, { [style.oddStyle]: oddStyle })}>
      <div
        className={classNames(
          style.teamSide,
          style[(gameOpponent1?.side as SideDota2 | SideLol) ?? '']
        )}
      >
        <HeroesIcon
          heroes={opponent1Heroes}
          placeholderSize={5}
          hidePercentage
          videogame={videogameSlug === 'dota-2' ? 'dota2' : 'lol'}
          type="pick"
          iconClassName={style.playerHero}
        />
        <div
          className={classNames(style.teamStats, {
            [style.winner]: winnerId === gameOpponent1?.id
          })}
        >
          {gameOpponent1 && (
            <>
              {gameOpponent1.id === forfeitedOpponent ? (
                <div className={style.forfeit}>{t('blocks_bracket_match_forfeit-long')}</div>
              ) : (
                <>
                  <div className={style.teamStat}>
                    <GoldIcon className={style.icon} />
                    {isNumber(gameOpponent1?.gold) && `${round(gameOpponent1.gold / 1000, 1)}k`}
                  </div>
                  <div className={style.teamStat}>
                    <KillIcon className={style.icon} />
                    {gameOpponent1?.kills}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div>
        <div className={style.timer}>{game.timer ? formatTimer(game.timer) : '--:--'}</div>
        <div className={style.winnerContainer}>
          <span>
            {winnerId === gameOpponent1?.id && (
              <CheckIcon className={classNames(style.icon, style.winnerIcon)} />
            )}
          </span>
          <span>
            <TrophyIcon className={style.icon} />
          </span>
          <span>
            {winnerId === gameOpponent2?.id && (
              <CheckIcon className={classNames(style.icon, style.winnerIcon)} />
            )}
          </span>
        </div>
      </div>
      <div
        className={classNames(
          style.teamSide,
          style[(gameOpponent2?.side as SideDota2 | SideLol) ?? '']
        )}
      >
        <HeroesIcon
          heroes={opponent2Heroes}
          placeholderSize={5}
          hidePercentage
          videogame={videogameSlug === 'dota-2' ? 'dota2' : 'lol'}
          orientation="reversed"
          type="pick"
          iconClassName={style.playerHero}
        />
        <div
          className={classNames(style.teamStats, style.teamStatsReversed, {
            [style.winner]: winnerId === gameOpponent2?.id
          })}
        >
          {gameOpponent2 && (
            <>
              {gameOpponent2.id === forfeitedOpponent ? (
                <div className={style.forfeit}>{t('blocks_bracket_match_forfeit-long')}</div>
              ) : (
                <>
                  <div className={style.teamStat}>
                    <GoldIcon className={style.icon} />
                    {isNumber(gameOpponent2?.gold) && `${round(gameOpponent2.gold / 1000, 1)}k`}
                  </div>
                  <div className={style.teamStat}>
                    <KillIcon className={style.icon} />
                    {gameOpponent2?.kills}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

BracketGameRecapMoba.displayName = 'BracketGameRecapMoba';

export default BracketGameRecapMoba;
